// ===================================
// ADMIN SESSIONS PAGE - STAGE 7
// src/pages/AdminSessions.js
// Full session management
// ===================================

import React, { useState, useEffect } from 'react';
import { getAdminSessions, getSessionDetails, updateSession } from '../services/adminApi';
import './AdminSessions.css';

const AdminSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', type: '', search: '' });
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadSessions();
  }, [filters.status, filters.type, pagination.offset]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await getAdminSessions({
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset
      });
      setSessions(response.sessions || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadSessions();
  };

  const handleViewSession = async (sessionId) => {
    try {
      setActionLoading(true);
      const response = await getSessionDetails(sessionId);
      setSelectedSession(response);
      setShowDetailModal(true);
    } catch (err) {
      alert('Failed to load session details');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (sessionId, newStatus) => {
    if (!window.confirm(`Change status to "${newStatus}"?`)) return;
    try {
      setActionLoading(true);
      await updateSession(sessionId, { status: newStatus });
      loadSessions();
      if (selectedSession?.session?.id === sessionId) {
        setSelectedSession(prev => ({ ...prev, session: { ...prev.session, status: newStatus } }));
      }
    } catch (err) {
      alert('Failed to update session');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusClass = (status) => ({
    completed: 'status-completed', active: 'status-active', paid: 'status-paid',
    pending: 'status-pending', expired: 'status-expired', cancelled: 'status-cancelled'
  }[status] || 'status-default');

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="admin-sessions-page">
      <div className="page-header">
        <div className="header-content">
          <h1>📋 Session Management</h1>
          <p>View and manage all interview sessions</p>
        </div>
        <div className="header-stats">
          <div className="stat-pill"><span className="stat-value">{pagination.total}</span><span className="stat-label">Total</span></div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <form onSubmit={handleSearch} className="filters-form">
          <div className="filter-row">
            <input
              type="text"
              placeholder="Search by user email..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="search-input"
            />
            <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="filter-select">
              <option value="">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="active">Active</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
            </select>
            <select value={filters.type} onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))} className="filter-select">
              <option value="">All Types</option>
              <option value="text">Text</option>
              <option value="voice">Voice</option>
              <option value="phone">Phone</option>
            </select>
            <button type="submit" className="btn btn-primary">Search</button>
          </div>
        </form>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Sessions Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Type</th><th>Duration</th><th>Status</th><th>Score</th><th>Created</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell"><div className="spinner"></div> Loading...</td></tr>
            ) : sessions.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No sessions found</td></tr>
            ) : (
              sessions.map(session => (
                <tr key={session.id}>
                  <td>
                    <div className="user-info">
                      <span className="user-email">{session.user_email || 'Unknown'}</span>
                      {session.company_name && <span className="company-name">@ {session.company_name}</span>}
                    </div>
                  </td>
                  <td><span className="type-badge">{session.interview_type}</span></td>
                  <td>{session.duration} min</td>
                  <td><span className={`status-badge ${getStatusClass(session.status)}`}>{session.status}</span></td>
                  <td>{session.score?.overall ? `${session.score.overall}%` : '-'}</td>
                  <td>{formatDate(session.created_at)}</td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleViewSession(session.id)} className="btn btn-sm btn-secondary">👁️</button>
                      {session.status === 'paid' && (
                        <button onClick={() => handleUpdateStatus(session.id, 'expired')} className="btn btn-sm btn-warning" title="Expire">⏰</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))} disabled={pagination.offset === 0} className="btn btn-sm">← Previous</button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))} disabled={currentPage >= totalPages} className="btn btn-sm">Next →</button>
        </div>
      )}

      {/* Session Detail Modal */}
      {showDetailModal && selectedSession && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Session Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="session-detail-grid">
                <div className="detail-section">
                  <h3>Session Info</h3>
                  <div className="detail-row"><span>ID:</span><span className="code">{selectedSession.session.id}</span></div>
                  <div className="detail-row"><span>User:</span><span>{selectedSession.session.user_email}</span></div>
                  <div className="detail-row"><span>Type:</span><span className="capitalize">{selectedSession.session.interview_type}</span></div>
                  <div className="detail-row"><span>Duration:</span><span>{selectedSession.session.duration} min</span></div>
                  <div className="detail-row"><span>Personality:</span><span className="capitalize">{selectedSession.session.interviewer_personality}</span></div>
                  <div className="detail-row"><span>Level:</span><span className="capitalize">{selectedSession.session.experience_level}</span></div>
                  <div className="detail-row"><span>Status:</span><span className={`status-badge ${getStatusClass(selectedSession.session.status)}`}>{selectedSession.session.status}</span></div>
                </div>
                <div className="detail-section">
                  <h3>Timing & Results</h3>
                  <div className="detail-row"><span>Created:</span><span>{formatDate(selectedSession.session.created_at)}</span></div>
                  <div className="detail-row"><span>Started:</span><span>{formatDate(selectedSession.session.started_at)}</span></div>
                  <div className="detail-row"><span>Ended:</span><span>{formatDate(selectedSession.session.ended_at)}</span></div>
                  {selectedSession.session.score && (
                    <div className="detail-row"><span>Score:</span><span className="score-large">{selectedSession.session.score.overall}%</span></div>
                  )}
                  {selectedSession.session.access_code && (
                    <div className="detail-row"><span>Access Code:</span><span className="code">{selectedSession.session.access_code}</span></div>
                  )}
                </div>
              </div>

              {/* Score Breakdown */}
              {selectedSession.session.score?.breakdown && (
                <div className="detail-section full-width">
                  <h3>Score Breakdown</h3>
                  <div className="score-breakdown-grid">
                    {Object.entries(selectedSession.session.score.breakdown).map(([key, value]) => (
                      <div key={key} className="breakdown-item">
                        <span className="breakdown-label">{key}</span>
                        <div className="breakdown-bar"><div className="breakdown-fill" style={{ width: `${value}%` }}></div></div>
                        <span className="breakdown-value">{value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transcript */}
              {selectedSession.session.transcript?.length > 0 && (
                <div className="detail-section full-width">
                  <h3>Transcript ({selectedSession.session.transcript.length} messages)</h3>
                  <div className="transcript-container">
                    {selectedSession.session.transcript.map((msg, i) => (
                      <div key={i} className={`transcript-message ${msg.role}`}>
                        <span className="message-role">{msg.role === 'assistant' ? '🎯 Interviewer' : '👤 Candidate'}</span>
                        <p className="message-content">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                {selectedSession.session.status === 'paid' && (
                  <button onClick={() => handleUpdateStatus(selectedSession.session.id, 'expired')} className="btn btn-warning" disabled={actionLoading}>Mark Expired</button>
                )}
                {selectedSession.session.status === 'active' && (
                  <button onClick={() => handleUpdateStatus(selectedSession.session.id, 'completed')} className="btn btn-success" disabled={actionLoading}>Mark Completed</button>
                )}
                <button onClick={() => setShowDetailModal(false)} className="btn btn-secondary">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSessions;
